import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Grid, Card, CardContent, Button, Chip,
  Alert, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tabs, Tab, FormControl, InputLabel, Select, MenuItem, IconButton, Tooltip,
} from '@mui/material';
import { Add, Edit, Delete, Autorenew, Warning, CheckCircle, Description } from '@mui/icons-material';
import { apiService } from '../../services/api';
import { displayName } from '../../types';

const CONTRACT_TYPES = ['permanent', 'fixed_term', 'part_time', 'contractor', 'internship', 'probation'];
const CONTRACT_STATUSES = ['active', 'expired', 'terminated', 'renewed'];

const typeLabel = (t: string) => t.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

const typeColor: Record<string, any> = {
  permanent: 'success',
  fixed_term: 'info',
  part_time: 'info',
  contractor: 'warning',
  internship: 'default',
  probation: 'warning',
};

const statusColor: Record<string, any> = {
  active: 'success',
  expired: 'error',
  terminated: 'default',
  renewed: 'info',
};

const daysUntil = (date: string) =>
  Math.ceil((new Date(date).getTime() - Date.now()) / (1000 * 60 * 60 * 24));

const fmtDate = (d: string) =>
  d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

const emptyForm = {
  employeeId: '', type: 'permanent', startDate: new Date().toISOString().split('T')[0],
  endDate: '', probationEndDate: '', alertDaysBefore: '30', contractRef: '', notes: '',
};

const Contracts: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [contracts, setContracts] = useState<any[]>([]);
  const [expiring, setExpiring] = useState<any[]>([]);
  const [expired, setExpired] = useState<any[]>([]);
  const [probation, setProbation] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [employees, setEmployees] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [dialog, setDialog] = useState<'create' | 'edit' | 'renew' | null>(null);
  const [selectedContract, setSelectedContract] = useState<any>(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [c, exp, expd, prob, s, emps] = await Promise.all([
        apiService.getContracts().catch(() => []),
        apiService.getExpiringContracts(60).catch(() => []),
        apiService.getExpiredContracts().catch(() => []),
        apiService.getProbationEndingSoon(30).catch(() => []),
        apiService.getContractStats().catch(() => null),
        apiService.getEmployees().catch(() => []),
      ]);
      setContracts(c);
      setExpiring(exp);
      setExpired(expd);
      setProbation(prob);
      setStats(s);
      setEmployees(emps);
    } catch { setError('Failed to load contracts'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const openCreate = () => {
    setSelectedContract(null);
    setForm({ ...emptyForm });
    setDialog('create');
  };

  const openEdit = (c: any) => {
    setSelectedContract(c);
    setForm({
      employeeId: c.employeeId, type: c.type,
      startDate: c.startDate || '', endDate: c.endDate || '',
      probationEndDate: c.probationEndDate || '',
      alertDaysBefore: String(c.alertDaysBefore ?? 30),
      contractRef: c.contractRef || '', notes: c.notes || '',
    });
    setDialog('edit');
  };

  const openRenew = (c: any) => {
    setSelectedContract(c);
    setForm({ ...emptyForm, employeeId: c.employeeId, type: c.type });
    setDialog('renew');
  };

  const save = async () => {
    if (!form.employeeId || !form.startDate) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        alertDaysBefore: Number(form.alertDaysBefore) || 30,
        endDate: form.endDate || undefined,
        probationEndDate: form.probationEndDate || undefined,
        contractRef: form.contractRef || undefined,
        notes: form.notes || undefined,
      };
      if (dialog === 'edit' && selectedContract) {
        await apiService.updateContract(selectedContract.id, payload);
        setSuccess('Contract updated');
      } else if (dialog === 'renew' && selectedContract) {
        await apiService.renewContract(selectedContract.id, payload);
        setSuccess('Contract renewed');
      } else {
        await apiService.createContract(payload);
        setSuccess('Contract created');
      }
      setDialog(null);
      fetchData();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to save contract');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this contract?')) return;
    try {
      await apiService.deleteContract(id);
      setSuccess('Contract deleted');
      fetchData();
    } catch { setError('Failed to delete'); }
  };

  if (loading) return (
    <Box display="flex" justifyContent="center" alignItems="center" height="400px">
      <CircularProgress />
    </Box>
  );

  const activeContracts = contracts.filter(c => c.status === 'active');

  const ContractTable = ({ rows, showAlert }: { rows: any[]; showAlert?: boolean }) => (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Employee</TableCell>
            <TableCell>Department</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Start</TableCell>
            <TableCell>End / Expiry</TableCell>
            <TableCell>Probation Ends</TableCell>
            <TableCell>Status</TableCell>
            {showAlert && <TableCell>Days Left</TableCell>}
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={showAlert ? 9 : 8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                No contracts found
              </TableCell>
            </TableRow>
          ) : rows.map(c => {
            const days = c.endDate ? daysUntil(c.endDate) : null;
            const isUrgent = days !== null && days <= 14;
            return (
              <TableRow key={c.id} hover
                sx={{ bgcolor: isUrgent ? 'rgba(211,47,47,0.04)' : undefined }}>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>
                    {c.employee ? displayName(c.employee) : '—'}
                  </Typography>
                  {c.contractRef && <Typography variant="caption" color="text.secondary">Ref: {c.contractRef}</Typography>}
                </TableCell>
                <TableCell>{c.employee?.department?.name || '—'}</TableCell>
                <TableCell>
                  <Chip label={typeLabel(c.type)} color={typeColor[c.type]} size="small" />
                </TableCell>
                <TableCell>{fmtDate(c.startDate)}</TableCell>
                <TableCell>
                  {c.endDate ? (
                    <Box display="flex" alignItems="center" gap={0.5}>
                      {isUrgent && <Warning fontSize="small" color="error" />}
                      <Typography variant="body2" color={isUrgent ? 'error' : 'text.primary'}>
                        {fmtDate(c.endDate)}
                      </Typography>
                    </Box>
                  ) : <Chip label="Open-ended" size="small" variant="outlined" color="success" />}
                </TableCell>
                <TableCell>
                  {c.probationEndDate ? (
                    <Typography variant="body2"
                      color={daysUntil(c.probationEndDate) <= 0 ? 'text.secondary' : daysUntil(c.probationEndDate) <= 14 ? 'warning.main' : 'text.primary'}>
                      {fmtDate(c.probationEndDate)}
                    </Typography>
                  ) : '—'}
                </TableCell>
                <TableCell>
                  <Chip label={c.status} color={statusColor[c.status] || 'default'} size="small" />
                </TableCell>
                {showAlert && (
                  <TableCell>
                    {days !== null ? (
                      <Chip label={`${days}d`} color={days <= 14 ? 'error' : days <= 30 ? 'warning' : 'info'} size="small" />
                    ) : '—'}
                  </TableCell>
                )}
                <TableCell align="right">
                  {c.status === 'active' && (
                    <Tooltip title="Renew contract">
                      <IconButton size="small" color="success" onClick={() => openRenew(c)}>
                        <Autorenew fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => openEdit(c)}>
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton size="small" color="error" onClick={() => handleDelete(c.id)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom>Employee Contracts</Typography>
          <Typography variant="body2" color="text.secondary">
            Track contract types, probation periods, and renewal deadlines
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={openCreate}
          sx={{ bgcolor: '#C08A2E', '&:hover': { bgcolor: '#A3501A' } }}>
          New Contract
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Stats */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {[
          { label: 'Active', value: stats?.active ?? 0, color: 'success.main' },
          { label: 'Expiring (60d)', value: stats?.expiringIn60Days ?? 0, color: 'warning.main' },
          { label: 'Expired (not closed)', value: stats?.expiredButActive ?? 0, color: 'error.main' },
          { label: 'Probation Ending (30d)', value: stats?.probationEndingSoon ?? 0, color: 'info.main' },
        ].map(s => (
          <Grid key={s.label} size={{ xs: 6, sm: 3 }}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" variant="body2">{s.label}</Typography>
                <Typography variant="h4" color={s.color}>{s.value}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Alerts */}
      {(stats?.expiredButActive ?? 0) > 0 && (
        <Alert severity="error" sx={{ mb: 2 }} icon={<Warning />}>
          <strong>{stats.expiredButActive} contract(s) have passed their end date</strong> but are still marked active — review and update.
        </Alert>
      )}
      {(stats?.expiringIn60Days ?? 0) > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>{stats.expiringIn60Days} contract(s) expiring within 60 days</strong> — schedule renewals.
        </Alert>
      )}
      {(stats?.probationEndingSoon ?? 0) > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>{stats.probationEndingSoon} employee(s) finishing probation within 30 days</strong> — confirm employment decisions.
        </Alert>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label={`All Active (${activeContracts.length})`} />
          <Tab label={`All Contracts (${contracts.length})`} />
          <Tab label={`Expiring Soon (${expiring.length})`} />
          <Tab label={`Expired (${expired.length})`} />
          <Tab label={`Probation Ending (${probation.length})`} />
        </Tabs>
      </Paper>

      {tab === 0 && <Paper><ContractTable rows={activeContracts} /></Paper>}
      {tab === 1 && <Paper><ContractTable rows={contracts} /></Paper>}
      {tab === 2 && <Paper><ContractTable rows={expiring} showAlert /></Paper>}
      {tab === 3 && (
        expired.length === 0
          ? <Paper><Box p={4} textAlign="center"><CheckCircle sx={{ fontSize: 48, color: 'success.main', mb: 1 }} /><Typography color="text.secondary">No expired contracts ✅</Typography></Box></Paper>
          : <Paper><ContractTable rows={expired} /></Paper>
      )}
      {tab === 4 && (
        probation.length === 0
          ? <Paper><Box p={4} textAlign="center"><CheckCircle sx={{ fontSize: 48, color: 'success.main', mb: 1 }} /><Typography color="text.secondary">No probation periods ending soon ✅</Typography></Box></Paper>
          : <Paper><ContractTable rows={probation} showAlert /></Paper>
      )}

      {/* Create / Edit / Renew Dialog */}
      <Dialog open={!!dialog} onClose={() => setDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialog === 'create' ? 'New Contract' : dialog === 'renew' ? `Renew — ${selectedContract?.employee ? displayName(selectedContract.employee) : ''}` : 'Edit Contract'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {dialog !== 'edit' && (
              <Grid size={12}>
                <FormControl fullWidth required>
                  <InputLabel>Employee</InputLabel>
                  <Select value={form.employeeId} onChange={e => setForm({ ...form, employeeId: e.target.value })} label="Employee">
                    {employees.map((emp: any) => <MenuItem key={emp.id} value={emp.id}>{displayName(emp)}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid size={6}>
              <FormControl fullWidth>
                <InputLabel>Contract Type</InputLabel>
                <Select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} label="Contract Type">
                  {CONTRACT_TYPES.map(t => <MenuItem key={t} value={t}>{typeLabel(t)}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={6}>
              <TextField fullWidth label="Contract Ref" value={form.contractRef}
                onChange={e => setForm({ ...form, contractRef: e.target.value })} />
            </Grid>
            <Grid size={6}>
              <TextField fullWidth label="Start Date" type="date" value={form.startDate}
                onChange={e => setForm({ ...form, startDate: e.target.value })}
                InputLabelProps={{ shrink: true }} required />
            </Grid>
            <Grid size={6}>
              <TextField fullWidth label="End Date" type="date" value={form.endDate}
                onChange={e => setForm({ ...form, endDate: e.target.value })}
                InputLabelProps={{ shrink: true }} helperText="Leave blank for permanent" />
            </Grid>
            <Grid size={6}>
              <TextField fullWidth label="Probation End Date" type="date" value={form.probationEndDate}
                onChange={e => setForm({ ...form, probationEndDate: e.target.value })}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid size={6}>
              <TextField fullWidth label="Alert Days Before Expiry" type="number" value={form.alertDaysBefore}
                onChange={e => setForm({ ...form, alertDaysBefore: e.target.value })} />
            </Grid>
            <Grid size={12}>
              <TextField fullWidth label="Notes" multiline rows={2} value={form.notes}
                onChange={e => setForm({ ...form, notes: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>Cancel</Button>
          <Button variant="contained" onClick={save}
            disabled={saving || !form.employeeId || !form.startDate}
            sx={{ bgcolor: '#C08A2E', '&:hover': { bgcolor: '#A3501A' } }}>
            {saving ? <CircularProgress size={20} /> : dialog === 'renew' ? 'Renew' : dialog === 'edit' ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Contracts;
